import type { en } from "./en";

export const es: typeof en = {
  announcement: {
    waitlistCta: "Unirme a FONDEA",
    waitlistCtaShort: "FONDEA",
    message: "Las empresas evolucionan. Su forma de operar también debe hacerlo.",
    cta: "Ver video",
    openVideo: "Reproducir video del anuncio",
    dismiss: "Ocultar anuncio",
    modalTitle: "Video de anuncio de IBYLIT",
  },
  a11y: {
    skipToContent: "Saltar al contenido principal",
    openMenu: "Abrir menú",
    closeMenu: "Cerrar menú",
    toggleTheme: "Cambiar tema",
    toggleLanguage: "Cambiar idioma",
    mainNavigation: "Navegación principal",
    footerNavigation: "Navegación del pie de página",
    accessibilityStatement:
      "Este sitio se diseña conforme a las pautas de accesibilidad WCAG 2.2 nivel AA y se mejora de forma continua.",
  },
  nav: {
    solutions: "Soluciones",
    services: "Servicios",
    solutionItems: {
      overview: "Visión general",
      banks: "Bancos e instituciones financieras",
      corporations: "Corporaciones",
      fintechs: "Fintechs y operadores",
      funds: "Fondos y fondeadores",
    },
    serviceItems: {
      overview: "Visión general",
      advisory: "Advisory y consultoría",
      implementation: "Implementación",
      turnkey: "Integraciones y proyectos turnkey",
      cloud: "Cloud Managed Services",
      support: "Mantenimiento, soporte y evolución",
      academy: "IBYLIT Academy",
    },
    platform: "Plataforma",
    products: "Productos",
    infrastructure: "Infraestructura",
    ecosystem: "Ecosistema",
    security: "Seguridad",
    resources: "Recursos",
    company: "Empresa",
    requestDemo: "Solicitar Demo",
    platformItems: {
      overview: "Visión General",
      financialOperations: "Operaciones Financieras",
      liquidityManagement: "Gestión de Liquidez",
      documentProcessing: "Procesamiento Documental",
      calculationEngine: "Motor de Cálculo",
      accountingAutomation: "Automatización Contable",
      integrations: "Integraciones",
    },
    productItems: {
      digitalFactoring: "Factoring Digital",
      reverseFactoring: "Confirming",
      b2bPayments: "Pagos B2B",
      invoiceAuctions: "Subastas de Facturas",
    },
    infrastructureItems: {
      architecture: "Arquitectura",
      cloudPlatform: "Plataforma Cloud",
      apis: "APIs",
      whiteLabel: "SOLVENN",
      scalability: "Escalabilidad",
    },
    ecosystemItems: {
      companies: "Empresas",
      funders: "Fondeadores",
      banks: "Bancos",
      corporations: "Corporaciones",
    },
    securityItems: {
      compliance: "Cumplimiento",
      securityArchitecture: "Arquitectura de Seguridad",
      audit: "Auditoría y Trazabilidad",
    },
    resourceItems: {
      blog: "Blog",
      documentation: "Documentación",
      caseStudies: "Casos de Éxito",
    },
    companyItems: {
      about: "Acerca de IBYLIT",
      mission: "Misión",
      vision: "Visión",
      contact: "Contacto",
    },
  },
  hero: {
    badge: "Plataforma Fintech B2B - Cloud",
    title: "La infraestructura que conecta bancos, empresas y fondeadores",
    titleHighlight: "para estructurar liquidez sobre activos reales",
    subtitle: "IBYLIT es el orquestador cloud que une ERPs, bancos, corporativos y fondeadores en un mismo flujo. No financiamos, no ejecutamos pagos: estructuramos el ecosistema para que la liquidez fluya.",
    cta1: "Explorar Plataforma",
    cta2: "Solicitar Demo",
    nodes: { companies: "Empresas", banks: "Bancos", funders: "Fondeadores", erps: "ERPs" },
  },
  problem: {
    badge: "El Problema",
    title: "La liquidez empresarial está rota",
    subtitle: "Las empresas enfrentan brechas de flujo de caja mientras las instituciones financieras buscan activos comerciales confiables — pero los procesos tradicionales son demasiado lentos y manuales.",
    items: [
      { title: "Ciclos de Pago Lentos", description: "Las empresas esperan 30–90 días por el pago de facturas, creando brechas de flujo de caja que limitan el crecimiento." },
      { title: "Procesos Manuales", description: "El financiamiento tradicional depende de flujos de trabajo en papel, aprobaciones manuales y comunicación fragmentada." },
      { title: "Sistemas Desconectados", description: "Las instituciones financieras carecen de una plataforma unificada para conectar empresas, fondeadores, ERPs y autoridades fiscales." },
    ],
  },
  solution: {
    badge: "La Solución",
    title: "Una infraestructura que estructura el flujo de liquidez",
    subtitle: "IBYLIT estructura el flujo entre quienes generan activos y quienes proveen capital, en una sola plataforma white-label.",
    bullets: [
      "Plataforma unificada para todos los participantes",
      "Despliegue white-label bajo tu propia marca",
      "Integración agnóstica al core existente",
      "Modular, embebible y orientada a API",
      "Trazabilidad de liquidación en tiempo real",
      "Seguridad y cumplimiento de nivel empresarial",
    ],
    steps: [
      "Las empresas registran sus activos",
      "IBYLIT valida y estructura",
      "Los fondeadores asignan capital",
      "Settlement trazable de extremo a extremo",
    ],
  },
  howItWorks: {
    badge: "Cómo Funciona",
    title: "Cuatro pasos hacia la liquidez digital",
    subtitle: "Automatización de extremo a extremo desde la carga de facturas hasta la entrega de capital.",
    step: "PASO",
    steps: [
      { title: "Cargar", description: "Las empresas registran activos en la plataforma." },
      { title: "Validar", description: "Los activos se verifican contra ERPs y fuentes oficiales." },
      { title: "Evaluar", description: "Los fondeadores evalúan oportunidades en tiempo real." },
      { title: "Liquidez", description: "Los fondeadores ejecutan; IBYLIT registra y traza el ciclo." },
    ],
  },
  products: {
    badge: "Productos",
    title: "Suite completa de productos de liquidez",
    subtitle: "Despliega productos financieros de clase mundial desde una sola plataforma.",
    items: [
      { title: "Factoring Digital", description: "Estructura el descuento de cuentas por cobrar conectando empresas con fondeadores verificados." },
      { title: "Confirming", description: "Programas de cadena de suministro bajo tu marca, con trazabilidad punto a punto." },
      { title: "Pagos B2B", description: "Orquesta y traza flujos de pago entre empresas; la ejecución la realizan los rieles bancarios." },
      { title: "Subastas de Facturas", description: "Mercados competitivos y verificables donde múltiples fondeadores asignan capital." },
    ],
  },
  platformCaps: {
    badge: "Plataforma",
    title: "Construida para operaciones financieras a escala",
    subtitle: "Capacidades centrales diseñadas para flujos de trabajo financieros empresariales.",
    items: [
      { title: "Orquestación Financiera", description: "Coordina el ciclo del instrumento desde su originación hasta el settlement, sin ejecutar." },
      { title: "Procesamiento Documental", description: "Validación automatizada de facturas, contratos y documentos financieros." },
      { title: "Motor de Cálculo", description: "Cálculos en tiempo real de tasas, intereses y comisiones." },
      { title: "Reconciliación Contable", description: "Genera asientos y conciliaciones para sistemas contables externos." },
      { title: "Motor de Integración", description: "Conectores listos para ERPs, cores bancarios y sistemas contables." },
      { title: "Notificaciones y Alertas", description: "Eventos en tiempo real sobre estado de instrumentos y aprobaciones." },
    ],
  },
  infrastructure: {
    badge: "Infraestructura",
    title: "Infraestructura cloud de nivel empresarial",
    subtitle: "Construida para escalabilidad, seguridad y confiabilidad desde el inicio.",
    items: [
      { title: "Cloud-Native", description: "Construida sobre AWS con auto-escalamiento, alta disponibilidad y despliegue global." },
      { title: "API-First", description: "APIs RESTful con documentación completa para integración transparente." },
      { title: "Arquitectura Modular", description: "Diseño basado en microservicios que permite escalamiento y despliegue independiente." },
      { title: "Segura por Diseño", description: "Cifrado de extremo a extremo, principios SOC2 y alineación ISO 27001." },
      { title: "Alto Rendimiento", description: "Procesamiento de transacciones en sub-segundos con SLA de 99.9% de disponibilidad." },
      { title: "Adaptable al Core", description: "Integración no invasiva con cores bancarios existentes — IBYLIT se adapta a tu infraestructura, no al revés." },
    ],
  },
  integrations: {
    badge: "Integraciones",
    title: "Conecta toda tu infraestructura financiera",
    subtitle: "Conectores preconstruidos para los sistemas que tu negocio ya utiliza.",
    items: [
      { title: "ERPs", description: "Sincronización bidireccional con sistemas de planificación de recursos.", tags: ["SAP", "Oracle", "NetSuite", "ERPs Custom"] },
      { title: "Cores Bancarios", description: "Conectividad directa con infraestructura bancaria.", tags: ["Core Banking", "Rieles de Pago", "SWIFT", "ACH"] },
      { title: "Autoridades Fiscales", description: "Validación en tiempo real contra sistemas gubernamentales.", tags: ["Facturación Electrónica", "Validación Fiscal", "Cumplimiento Fiscal"] },
      { title: "Contabilidad", description: "Asientos contables automatizados y conciliación.", tags: ["QuickBooks", "Xero", "GL Custom"] },
    ],
  },
  ecosystem: {
    badge: "Ecosistema",
    title: "Un ecosistema financiero conectado",
    subtitle: "IBYLIT conecta a todos los participantes de la cadena de liquidez a través de una sola plataforma.",
    chain: ["Empresas", "Activos", "IBYLIT", "Fondeadores", "Liquidez"],
    participants: [
      { title: "Bancos", description: "Lanzan factoring, confirming y subastas bajo su marca, integrados a su core, sin reemplazar infraestructura." },
      { title: "Corporativos", description: "Estructuran programas de pago a proveedores y optimizan capital de trabajo con trazabilidad punto a punto." },
      { title: "PYMEs", description: "Acceden a liquidez sobre sus facturas a través de la red de fondeadores conectados, sin fricción operativa." },
      { title: "Fondeadores", description: "Acceso estructurado a oportunidades verificadas, con custodia operativa, subastas y settlement trazable.", bullets: ["Acceso estructurado a oportunidades", "Custodia operativa de instrumentos", "Subastas verificables", "Settlement con trazabilidad"], disclaimer: "Registro es interno — no constituye custodia regulada." },
    ],
  },
  annotation: {
    badge: "Anotación & Custodia Operativa",
    title: "Lifecycle del instrumento, registrado y trazado",
    subtitle: "IBYLIT registra cada instrumento financiero desde su origen hasta su liquidación con trazabilidad operativa completa.",
    items: [
      { title: "Anotación en cuenta", description: "Registro digital del instrumento con identificador único y metadata operativa." },
      { title: "Custodia operativa", description: "Resguardo lógico del estado del instrumento durante todo su ciclo de vida." },
      { title: "Lifecycle trazable", description: "Eventos de originación, cesión, fondeo y liquidación quedan registrados de forma inmutable." },
    ],
    disclaimer: "La anotación y custodia operativa de IBYLIT son funciones internas de plataforma y no constituyen custodia regulada por entidad autorizada.",
  },
  securitySection: {
    badge: "Seguridad",
    title: "Seguridad y confianza",
    subtitle: "Arquitectura de seguridad-primero para proteger cada operación financiera.",
    items: [
      { title: "Cifrado TLS 1.2 / 1.3", description: "Todo el tráfico se transmite sobre HTTPS con cifrado de extremo a extremo." },
      { title: "Infraestructura AWS", description: "Despliegue global sobre AWS con distribución vía CloudFront y aislamiento por región." },
      { title: "Protección anti-bot", description: "Cloudflare Turnstile valida cada interacción crítica para prevenir abuso automatizado." },
      { title: "Rate limiting avanzado", description: "Límites por IP, dispositivo y sesión que mitigan ataques de fuerza bruta y abuso." },
      { title: "Monitoreo continuo", description: "Telemetría, alertas y trazas de auditoría 24/7 sobre toda la plataforma." },
      { title: "Principios SOC 2", description: "Controles operativos alineados con SOC 2 Tipo II e ISO 27001." },
    ],
    scene: {
      people: "Personas y accesos",
      perimeter: "Controles de perímetro",
      infra: "Infraestructura y código",
      monitor: "Telemetría en vivo",
    },
  },
  cta: {
    title: "Transforma tu infraestructura financiera",
    subtitle: "Solicita una demo para explorar cómo IBYLIT habilita liquidez digital para bancos, fintechs y corporaciones.",
    cta1: "Solicitar Demo",
    cta2: "Explorar Plataforma",
  },
  footer: {
    tagline: "Infraestructura para la orquestación de liquidez financiera.",
    eyebrow: "INFRAESTRUCTURA FINANCIERA",
    description: "Ibylit conecta empresas, bancos, ERPs y fondeadores para operar soluciones de liquidez digital bajo una arquitectura unificada.",
    ctaDemo: "Solicitar Demo",
    follow: "Síguenos",
    badges: ["CLOUD-NATIVE", "API-FIRST", "MODULAR", "ESCALABLE"],
    copyright: "Todos los derechos reservados.",
    privacy: "Política de Privacidad",
    terms: "Términos de Servicio",
    categories: {
      Platform: "Plataforma",
      Products: "Productos",
      Infrastructure: "Infraestructura",
      Security: "Seguridad",
      Company: "Empresa",
    },
    links: {
      overview: "Visión General",
      financialOperations: "Operaciones Financieras",
      integrations: "Integraciones",
      digitalFactoring: "Factoring Digital",
      reverseFactoring: "Confirming",
      b2bPayments: "Pagos B2B",
      architecture: "Arquitectura",
      cloudPlatform: "Plataforma Cloud",
      apis: "APIs",
      compliance: "Cumplimiento",
      audit: "Auditoría y Trazabilidad",
      about: "Acerca de",
      contact: "Contacto",
    },
    columns: {
      products: {
        title: "Productos",
        items: [
          { name: "Factoring Digital", desc: "Liquidez inmediata para cuentas por cobrar", href: "/products/digital-factoring" },
          { name: "Confirming", desc: "Financiamiento de cadena de suministro", href: "/products/reverse-factoring" },
          { name: "Pagos B2B", desc: "Pagos empresariales en tiempo real", href: "/products/b2b-payments" },
          { name: "Subastas de Instrumentos", desc: "Mercados competitivos de financiamiento", href: "/products/invoice-auctions" },
          { name: "Anotación en Cuenta", desc: "Registro electrónico de instrumentos", href: "/products/annotation" },
          { name: "Custodia Operativa", desc: "Gestión y control de instrumentos", href: "/products/custody" },
        ],
      },
      platform: {
        title: "Plataforma",
        items: [
          { name: "Visión General", href: "/platform/overview" },
          { name: "Operaciones Financieras", href: "/platform/financial-operations" },
          { name: "Integraciones", href: "/platform/integrations" },
          { name: "Casos de Uso", href: "/platform/use-cases" },
          { name: "Recursos", href: "/platform/resources" },
          { name: "Documentación", href: "/platform/docs" },
          { name: "Marco regulatorio", href: "/platform/regulatory-framework" },
        ],
      },
      infrastructure: {
        title: "Infraestructura",
        items: [
          { name: "Arquitectura", href: "/infrastructure/architecture" },
          { name: "Plataforma Cloud", href: "/infrastructure/cloud-platform" },
          { name: "APIs", href: "/infrastructure/apis" },
          { name: "Disponibilidad", href: "/infrastructure/availability" },
          { name: "Rendimiento", href: "/infrastructure/performance" },
        ],
      },
      security: {
        title: "Seguridad",
        items: [
          { name: "Cumplimiento", href: "/security/compliance" },
          { name: "Seguridad de Datos", href: "/security/data" },
          { name: "Privacidad", href: "/security/privacy" },
          { name: "Políticas", href: "/security/policies" },
        ],
      },
      company: {
        title: "Empresa",
        items: [
          { name: "Acerca de Ibylit", href: "/company/about" },
          { name: "Nuestro Equipo", href: "/company/team" },
          { name: "Contacto", href: "/company/contact" },
          { name: "Alianzas", href: "/company/partners" },
          { name: "Trabaja con Nosotros", href: "/company/careers" },
          { name: "Política de Privacidad", href: "/legal/privacy" },
          { name: "Política de Cookies", href: "/legal/cookies" },
          { name: "Términos de Uso", href: "/legal/terms" },
        ],
      },
    },
  },
  subpage: {
    notFound: "Página No Encontrada",
    backHome: "Volver al Inicio",
    requestDemo: "Solicitar Demo",
  },
  pages: {
    platform: {
      overview: {
        title: "Visión General de la Plataforma",
        subtitle: "Una plataforma completa de infraestructura financiera para operaciones de liquidez digital.",
        content: [
          "IBYLIT es una plataforma de infraestructura financiera B2B white-label cloud-native que permite a bancos, fintechs y corporaciones operar soluciones de liquidez digital.",
          "La plataforma proporciona gestión de operaciones financieras de extremo a extremo, desde el procesamiento de facturas hasta la liquidación, todo bajo tu propia marca.",
          "Con arquitectura modular y APIs completas, IBYLIT se integra sin problemas en tu stack tecnológico existente.",
        ],
      },
      "financial-operations": {
        title: "Operaciones Financieras",
        subtitle: "Gestiona el ciclo completo de transacciones financieras.",
        content: [
          "Desde la originación hasta la liquidación, IBYLIT automatiza cada paso del flujo de trabajo de operaciones financieras.",
          "Seguimiento de transacciones en tiempo real, aprobaciones automatizadas y reportes completos.",
          "Soporte multi-moneda con reglas de negocio configurables y controles de cumplimiento.",
        ],
      },
      "liquidity-management": {
        title: "Gestión de Liquidez",
        subtitle: "Optimiza el capital de trabajo y flujo de caja en toda tu organización.",
        content: [
          "Visibilidad en tiempo real de posiciones de caja, cuentas por cobrar y por pagar.",
          "Pronóstico automatizado de flujo de caja con capacidades de modelado de escenarios.",
          "Integración con sistemas de gestión de tesorería para vistas unificadas de liquidez.",
        ],
      },
      "document-processing": {
        title: "Procesamiento Documental",
        subtitle: "Validación y procesamiento automatizado de documentos financieros.",
        content: [
          "Validación de facturas con IA contra bases de datos de autoridades fiscales.",
          "Clasificación, extracción y verificación automatizada de documentos.",
          "Soporte para estándares de facturación electrónica en múltiples jurisdicciones.",
        ],
      },
      "calculation-engine": {
        title: "Motor de Cálculo Financiero",
        subtitle: "Cálculos financieros en tiempo real para todas las operaciones de liquidez.",
        content: [
          "Cálculos de tasas de descuento configurables con múltiples modelos de precio.",
          "Computación de intereses en tiempo real, cálculo de comisiones y análisis de spread.",
          "Soporte para estructuras financieras complejas incluyendo financiamiento por tramos.",
        ],
      },
      "accounting-automation": {
        title: "Automatización Contable",
        subtitle: "Asientos contables automatizados y conciliación financiera.",
        content: [
          "Generación automatizada de asientos contables para todas las transacciones financieras.",
          "Conciliación en tiempo real entre transacciones de la plataforma y registros contables.",
          "Integración con los principales sistemas contables y libros mayores.",
        ],
      },
      integrations: {
        title: "Integraciones",
        subtitle: "Conecta toda tu infraestructura tecnológica financiera.",
        content: [
          "Conectores preconstruidos para SAP, Oracle, NetSuite y otros ERPs principales.",
          "Integraciones con cores bancarios para rieles de pago, SWIFT y ACH.",
          "APIs de autoridades fiscales para validación de facturas en tiempo real y cumplimiento fiscal.",
        ],
      },
      "use-cases": {
        title: "Casos de Uso",
        subtitle: "Bancos lanzan factoring white-label, corporativos despliegan confirming, fondeadores acceden a portfolio verificado.",
        content: [
          "Los bancos usan IBYLIT para lanzar productos de factoring y confirming bajo su propia marca, sin reconstruir su core.",
          "Los corporativos despliegan programas de financiamiento de cadena de suministro que fortalecen relaciones con proveedores mientras optimizan su capital de trabajo.",
          "Los fondeadores acceden de forma estructurada a portafolios verificados de instrumentos, con custodia operativa y liquidación trazable.",
          "Cada caso de uso se entrega como un workflow configurable sobre la misma plataforma de orquestación — no como un producto separado.",
        ],
      },
      resources: {
        title: "Recursos",
        subtitle: "Materiales para arquitectos de soluciones, equipos de producto y stakeholders financieros.",
        content: [
          "Nuestro centro de recursos reúne briefs de solución, descripciones de arquitectura y playbooks operativos para equipos que evalúan o despliegan IBYLIT.",
          "Los materiales están organizados por audiencia: arquitectos técnicos, product owners y stakeholders de tesorería / riesgo.",
          "Los materiales detallados se comparten durante engagements estructurados de evaluación — solicita acceso en soporte@ibylit.com.",
        ],
      },
      docs: {
        title: "Documentación",
        subtitle: "Guías técnicas, referencias de API y ejemplos de integración — portal de desarrolladores próximamente.",
        content: [
          "IBYLIT proporciona documentación técnica que cubre conceptos de plataforma, referencias de APIs REST, eventos de webhook y patrones de integración.",
          "Se mantienen implementaciones de referencia y payloads de ejemplo para los escenarios de integración más frecuentes.",
          "Un portal de desarrolladores self-serve está en el roadmap; mientras tanto, la documentación se entrega a los equipos de integración durante el onboarding.",
        ],
      },
    },
    products: {
      "digital-factoring": {
        title: "Factoring Digital",
        subtitle: "Convierte cuentas por cobrar en liquidez inmediata mediante financiamiento automatizado de facturas.",
        content: [
          "El Factoring Digital permite a las empresas vender sus cuentas por cobrar con descuento para acceder a flujo de caja inmediato.",
          "La plataforma automatiza todo el proceso: carga de facturas, validación, emparejamiento con fondeadores y liquidación.",
          "Beneficios para empresas: liquidez inmediata, sin deuda en balance y flujo de caja predecible.",
          "Beneficios para fondeadores: acceso a activos comerciales verificados con evaluación de riesgo transparente.",
        ],
      },
      "reverse-factoring": {
        title: "Confirming / Factoring Inverso",
        subtitle: "Habilita programas de financiamiento de cadena de suministro bajo tu marca.",
        content: [
          "El Confirming permite a grandes corporaciones ayudar a sus proveedores a acceder a pagos anticipados.",
          "La empresa compradora confirma las facturas, permitiendo a los proveedores recibir financiamiento a mejores tasas.",
          "Fortalece las relaciones de cadena de suministro mientras optimiza el capital de trabajo para todas las partes.",
          "El despliegue white-label permite a los bancos ofrecer programas de confirming a sus clientes corporativos.",
        ],
      },
      "b2b-payments": {
        title: "Pagos B2B",
        subtitle: "Automatiza flujos de pagos entre empresas con liquidación en tiempo real.",
        content: [
          "Procesamiento optimizado de pagos B2B con conciliación y reportes automatizados.",
          "Soporte para múltiples métodos de pago, monedas y plazos de liquidación.",
          "Integración con rieles de pago bancarios para transferencias eficientes de fondos.",
        ],
      },
      "invoice-auctions": {
        title: "Subastas de Facturas",
        subtitle: "Crea mercados competitivos para financiamiento de facturas.",
        content: [
          "Permite a múltiples fondeadores competir por oportunidades de financiamiento de facturas.",
          "Mecanismos de subasta transparentes que optimizan precios para las empresas.",
          "Pujas en tiempo real con ejecución y liquidación automatizada.",
        ],
      },
      annotation: {
        title: "Anotación en Cuenta",
        subtitle: "Registro electrónico estructurado de instrumentos financieros con identificador único y metadata operativa.",
        content: [
          "La Anotación en Cuenta proporciona un registro digital estructurado de cada instrumento financiero gestionado en la plataforma — facturas, órdenes de pago, contratos de cesión.",
          "Cada instrumento recibe un identificador único persistente, metadata operativa enriquecida (emisor, deudor, monto, vencimiento, estado) y trazabilidad completa de eventos.",
          "Esto permite a bancos, fondeadores y corporativos operar con un libro de instrumentos consistente, auditable y consultable vía API en tiempo real.",
          "Disclaimer regulatorio: la Anotación en Cuenta es un registro interno de la plataforma — no constituye custodia regulada de valores ni reemplaza a entidades autorizadas por reguladores locales.",
        ],
      },
      custody: {
        title: "Custodia Operativa",
        subtitle: "Resguardo lógico del estado del instrumento, control de posiciones y trazabilidad operativa end-to-end.",
        content: [
          "La Custodia Operativa gestiona el ciclo de vida lógico de cada instrumento financiero: estado actual, titular vigente, eventos de cesión, cambios de posición y liquidación.",
          "Permite a fondeadores y operadores controlar sus posiciones consolidadas, conciliar movimientos y exportar evidencia operativa para reportes internos o auditoría.",
          "Toda operación queda registrada con marca de tiempo inmutable, identidad del actor y contexto de negocio — habilitando auditabilidad completa.",
          "Disclaimer regulatorio: la Custodia Operativa es un servicio tecnológico de resguardo lógico — no constituye custodia regulada de valores. La custodia regulada queda en manos de entidades habilitadas por el regulador correspondiente.",
        ],
      },
    },
    infrastructure: {
      architecture: {
        title: "Arquitectura",
        subtitle: "Arquitectura de microservicios cloud-native construida para operaciones financieras.",
        content: [
          "Arquitectura basada en microservicios con capacidades de escalamiento y despliegue independiente.",
          "Diseño orientado a eventos para procesamiento de transacciones y notificaciones en tiempo real.",
          "Construida sobre AWS con despliegue multi-región para alta disponibilidad.",
        ],
      },
      "cloud-platform": {
        title: "Plataforma Cloud",
        subtitle: "Infraestructura cloud de nivel empresarial en AWS.",
        content: [
          "Plataforma cloud completamente gestionada con auto-escalamiento y SLA de 99.9% de disponibilidad.",
          "Distribución CDN global para acceso de baja latencia en todo el mundo.",
          "Respaldos automatizados, recuperación ante desastres y monitoreo de infraestructura.",
        ],
      },
      apis: {
        title: "APIs",
        subtitle: "APIs RESTful completas para integración transparente.",
        content: [
          "Conecta en minutos mediante APIs REST estándar y webhooks. Sin SDKs, sin configuraciones complejas.",
          "Arquitectura 100% cloud: cero instalaciones, cero mantenimiento de librerías de terceros.",
          "Ambiente sandbox + documentación interactiva para pruebas inmediatas.",
        ],
      },
      "white-label": {
        title: "SOLVENN",
        subtitle: "Despliega bajo tu propia marca con personalización completa. Adaptable a tu core existente.",
        content: [
          "Personalización total de marca incluyendo logos, colores, dominios y plantillas de email.",
          "Ambientes multi-tenant aislados con recursos dedicados por cliente.",
          "Flujos de trabajo y reglas de negocio personalizables por despliegue.",
          "Arquitectura agnóstica al core: IBYLIT se integra de forma no invasiva con tu core bancario, ERP o sistema financiero existente — sin necesidad de reemplazar lo que ya funciona.",
          "Tus clientes ven tu marca. Tu equipo controla la experiencia. IBYLIT impulsa la infraestructura detrás de escena.",
        ],
      },
      scalability: {
        title: "Escalabilidad",
        subtitle: "Construida para crecer con tu negocio desde el día uno.",
        content: [
          "Escalamiento horizontal con microservicios en contenedores.",
          "Particionamiento de base de datos y réplicas de lectura para operaciones de alto rendimiento.",
          "Optimización de rendimiento para millones de transacciones por día.",
        ],
      },
      availability: {
        title: "Disponibilidad",
        subtitle: "SLA 99.9%, despliegue multi-región AWS, recuperación ante desastres y monitoreo continuo.",
        content: [
          "IBYLIT opera con un SLA de disponibilidad del 99.9% en sus ambientes productivos.",
          "Las cargas se despliegan en múltiples regiones AWS con failover automatizado, respaldos cifrados y procedimientos probados de recuperación ante desastres.",
          "La salud de la plataforma se monitorea de forma continua, con cobertura on-call y alertamiento proactivo sobre latencia, tasa de error y saturación.",
        ],
      },
      performance: {
        title: "Rendimiento",
        subtitle: "Procesamiento sub-segundo, escalamiento horizontal y optimización para millones de transacciones diarias.",
        content: [
          "Las rutas operacionales críticas — creación de instrumentos, validación, matching de subastas y liquidación — están diseñadas para responder con latencia sub-segundo.",
          "La plataforma escala horizontalmente conforme crece el volumen transaccional, sin necesidad de rediseño arquitectónico.",
          "El rendimiento se valida contra cargas representativas de millones de instrumentos por día, asegurando holgura para operadores enterprise.",
        ],
      },
      roadmap: {
        title: "Roadmap",
        subtitle: "Próximas capacidades: nuevos rieles de pago, integraciones adicionales de ERPs y expansión de mercados verticales.",
        content: [
          "Nuestro roadmap se estructura en tres ejes: conectividad (nuevos rieles de pago e integraciones de ERPs/cores), profundidad de producto (nuevos instrumentos y workflows) y geografía (expansión en LATAM y mercados seleccionados de EMEA).",
          "Las capacidades específicas se priorizan junto con clientes ancla, dando preferencia a lo que desbloquea valor operativo medible.",
          "Las discusiones detalladas de roadmap están disponibles bajo NDA para prospectos calificados y partners.",
        ],
      },
    },
    ecosystem: {
      companies: {
        title: "Para Empresas",
        subtitle: "Accede a liquidez inmediata convirtiendo tus cuentas por cobrar en efectivo.",
        content: [
          "Carga facturas digitalmente y accede a financiamiento en horas.",
          "Precios transparentes con tasas competitivas de múltiples fondeadores.",
          "Dashboard para rastrear todas las transacciones, pagos y flujo de caja.",
        ],
      },
      funders: {
        title: "Para Fondeadores",
        subtitle: "Accede a activos comerciales verificados con evaluación de riesgo transparente.",
        content: [
          "Portafolio curado de facturas verificadas de empresas solventes.",
          "Herramientas de evaluación de riesgo en tiempo real y gestión de portafolio.",
          "Liquidación automatizada y reportes completos.",
        ],
      },
      banks: {
        title: "Para Bancos",
        subtitle: "Despliega programas de factoring y confirming white-label sobre tu core existente.",
        content: [
          "Lanza servicios de factoring digital bajo tu propia marca.",
          "Integra con tu core bancario y sistemas de cumplimiento existentes — IBYLIT se adapta a tu infraestructura.",
          "Expande tu oferta de productos sin construir infraestructura desde cero.",
          "Sin reemplazo de core necesario: IBYLIT se monta sobre tus sistemas existentes como una capa de producto.",
        ],
      },
      corporations: {
        title: "Para Corporaciones",
        subtitle: "Lanza programas de financiamiento de cadena de suministro para fortalecer relaciones con proveedores.",
        content: [
          "Implementa programas de confirming que benefician a toda tu cadena de suministro.",
          "Mejora los términos de pago a proveedores mientras optimizas tu propio capital de trabajo.",
          "Onboarding digital y gestión de redes de proveedores.",
        ],
      },
    },
    security: {
      compliance: {
        title: "Cumplimiento",
        subtitle: "Construida con cumplimiento regulatorio en su núcleo.",
        content: [
          "Principios SOC 2 Tipo II integrados en el diseño y operaciones de la plataforma.",
          "Alineación ISO 27001 para gestión de seguridad de la información.",
          "Reglas de cumplimiento configurables para diferentes jurisdicciones y regulaciones.",
        ],
      },
      architecture: {
        title: "Arquitectura de Seguridad",
        subtitle: "Seguridad multicapa para operaciones financieras.",
        content: [
          "Cifrado de extremo a extremo para todos los datos en tránsito y en reposo (calificación SSL A+).",
          "Autenticación multifactor y control de acceso basado en roles.",
          "Segmentación de red y sistemas de detección de intrusiones.",
        ],
      },
      audit: {
        title: "Auditoría y Trazabilidad",
        subtitle: "Trazabilidad completa de transacciones con registros de auditoría inmutables.",
        content: [
          "Cada acción se registra con marca de tiempo, identidad del usuario y contexto.",
          "Trazas de auditoría inmutables para cumplimiento regulatorio y resolución de disputas.",
          "Reportes exportables para requisitos de auditoría interna y externa.",
        ],
      },
      data: {
        title: "Seguridad de Datos",
        subtitle: "Cifrado AES-256 en reposo, TLS 1.3 en tránsito, gestión de claves con HSM.",
        content: [
          "Todos los datos gestionados por IBYLIT se cifran en reposo con AES-256 y en tránsito con TLS 1.3.",
          "Las claves criptográficas se gestionan en Hardware Security Modules (HSM) dedicados con estricta separación de funciones.",
          "El acceso a datos productivos está gobernado por políticas de mínimo privilegio, autenticación multifactor y registro de auditoría completo.",
        ],
      },
      privacy: {
        title: "Privacidad",
        subtitle: "Tratamiento de datos alineado con GDPR y LGPD, residencia configurable y derecho al olvido.",
        content: [
          "IBYLIT opera bajo marcos de privacidad alineados con GDPR (UE) y LGPD (Brasil), con controles aplicables transversalmente entre jurisdicciones.",
          "La residencia de datos es configurable por despliegue, permitiendo a cada operador alojar la información en la región exigida por su regulador o negocio.",
          "Los derechos del titular — acceso, rectificación, supresión y portabilidad — se atienden mediante procedimientos operativos documentados.",
        ],
      },
      certifications: {
        title: "Certificaciones",
        subtitle: "Alineación SOC 2 Tipo II, ISO 27001 y pruebas de penetración periódicas.",
        content: [
          "Las operaciones de IBYLIT están diseñadas en alineación con los marcos de control SOC 2 Tipo II e ISO 27001.",
          "Se ejecutan pruebas de penetración independientes de forma recurrente, con remediación gestionada bajo un programa formal de vulnerabilidades.",
          "La evidencia de cumplimiento y los cuestionarios de seguridad están disponibles bajo NDA durante procesos de evaluación enterprise.",
        ],
      },
      policies: {
        title: "Políticas",
        subtitle: "Divulgación responsable, gestión de incidentes y políticas de retención de datos.",
        content: [
          "Operamos un programa de divulgación responsable para investigadores de seguridad — los reportes pueden enviarse a soporte@ibylit.com.",
          "La respuesta a incidentes de seguridad sigue un playbook documentado que cubre detección, contención, comunicación y revisión post-mortem.",
          "Los plazos de retención y eliminación de datos están definidos por categoría y alineados con los términos contractuales del cliente y las obligaciones regulatorias.",
        ],
      },
    },
    resources: {
      blog: {
        title: "Blog",
        subtitle: "Perspectivas sobre tecnología financiera, liquidez e infraestructura digital.",
        content: ["Próximamente — mantente atento a artículos sobre infraestructura fintech, gestión de liquidez y tendencias de la industria."],
      },
      documentation: {
        title: "Documentación",
        subtitle: "Documentación técnica para desarrolladores e integradores.",
        content: ["Documentación de API, guías de integración y referencias técnicas estarán disponibles pronto."],
      },
      "case-studies": {
        title: "Casos de Éxito",
        subtitle: "Descubre cómo las organizaciones usan IBYLIT para transformar sus operaciones financieras.",
        content: ["Casos de éxito mostrando implementaciones reales se publicarán pronto."],
      },
    },
    company: {
      about: {
        title: "Acerca de IBYLIT",
        subtitle: "Construimos la infraestructura que conecta empresas y capital.",
        content: [
          "IBYLIT es una empresa costarricense de infraestructura tecnológica financiera. Opera dos líneas complementarias: FONDEA, que acerca nuevas alternativas de liquidez a las empresas, y SOLVENN, que permite a las instituciones financieras modernizar sus operaciones bajo su propia marca, con visión regional para América Latina.",
          "IBYLIT desarrolla tecnología para que bancos, entidades financieras, fintechs y corporaciones puedan ofrecer soluciones digitales de capital de trabajo bajo su propia marca.",
          "Nuestra plataforma conecta a empresas, pagadores y financiadores en un mismo entorno para gestionar procesos como factoring, confirming, pago a proveedores, subastas de facturas y otros productos de liquidez. Desde la incorporación de participantes y la validación de documentos hasta la operación y el seguimiento, ayudamos a que cada flujo sea más ágil, trazable y accesible.",
          "No somos quienes financian las operaciones. Proveemos la infraestructura que permite a las instituciones y empresas organizar sus procesos, conectar a sus participantes y operar soluciones de liquidez según sus propias reglas.",
          "Trabajamos con organizaciones que buscan modernizar sus servicios financieros, ampliar sus capacidades digitales y atender mejor las necesidades de capital de trabajo de las empresas, sin tener que construir cada solución desde cero.",
          "Nuestra misión: democratizar el acceso a la liquidez empresarial mediante tecnología que conecta empresas, instituciones financieras y fuentes de financiamiento.",
          "Lo que hacemos posible:",
          "Operar bajo su propia marca: soluciones white-label alineadas con la identidad y los procesos de cada organización.",
          "Conectar a los participantes: empresas, pagadores y financiadores en un entorno digital común.",
          "Gestionar más que una transacción: flujos de trabajo para factoring, confirming, pagos B2B y otras soluciones de capital de trabajo.",
          "Integrarse con el ecosistema existente: tecnología diseñada para convivir con los sistemas y las reglas de cada institución.",
          "Mejorar la trazabilidad: información y seguimiento de las operaciones a lo largo de su ciclo.",
          "IBYLIT y FONDEA: IBYLIT ofrece infraestructura tecnológica para que otras organizaciones operen soluciones financieras bajo su propia marca. FONDEA, por su parte, conecta empresas con alternativas de liquidez y financiamiento dentro de un ecosistema digital.",
          "IBYLIT construye la infraestructura. FONDEA la acerca a las empresas.",
        ],
      },
      mission: {
        title: "Nuestra Misión",
        subtitle: "Democratizar el acceso a la liquidez empresarial a través de la tecnología.",
        content: [
          "Creemos que toda empresa merece acceso a soluciones de liquidez eficientes, transparentes y accesibles.",
          "Al construir infraestructura financiera abierta, habilitamos a las instituciones a servir mejor y más rápido a sus clientes.",
        ],
      },
      vision: {
        title: "Nuestra Visión",
        subtitle: "Un mundo donde la liquidez empresarial fluye tan libremente como la información.",
        content: [
          "Visualizamos un futuro donde las facturas son instantáneamente convertibles en capital, las cadenas de suministro son financieramente resilientes, y cada empresa tiene acceso a la liquidez que necesita para crecer.",
        ],
      },
      contact: {
        title: "Contáctanos",
        subtitle: "Ponte en contacto para conocer más sobre IBYLIT.",
        content: [
          "Email: soporte@ibylit.com",
          "Solicita una demo para ver la plataforma en acción y discutir cómo IBYLIT puede servir a tu organización.",
        ],
      },
      team: {
        title: "Nuestro Equipo",
        subtitle: "Liderazgo con experiencia profunda en banca, fintech y tecnología cloud-native enterprise.",
        content: [
          "IBYLIT es construido por un equipo que ha pasado décadas operando en la intersección de servicios financieros y software empresarial.",
          "Nuestro liderazgo combina experiencia directa lanzando productos bancarios, escalando plataformas fintech y arquitecturando sistemas cloud-native para industrias reguladas.",
          "Aplicamos esa experiencia en cada despliegue — operando como proveedores de infraestructura de largo plazo, no como un vendor de proyecto único.",
        ],
      },
      partners: {
        title: "Alianzas",
        subtitle: "Programa para integradores, consultoras de tesorería y proveedores de tecnología bancaria.",
        content: [
          "El Programa de Partners de IBYLIT permite a integradores de sistemas, consultoras de tesorería y proveedores de core bancario entregar soluciones de liquidez a sus clientes.",
          "Los partners reciben habilitación técnica, soporte conjunto de go-to-market y acceso a un playbook estructurado de despliegue.",
          "Trabajamos con partners seleccionados en LATAM y EMEA para escalar infraestructura financiera white-label de forma responsable.",
        ],
      },
      press: {
        title: "Prensa",
        subtitle: "Recursos para periodistas, comunicados oficiales y kit de marca descargable.",
        content: [
          "Las consultas de prensa, comunicados oficiales y briefings ejecutivos se coordinan a través de nuestro equipo de comunicaciones.",
          "Periodistas que cubren infraestructura financiera, embedded finance o liquidez B2B pueden solicitar entrevistas con el liderazgo de IBYLIT.",
          "Los activos de marca — logos, paleta de colores e imágenes de producto — están disponibles a solicitud en soporte@ibylit.com.",
        ],
      },
      careers: {
        title: "Trabaja con Nosotros",
        subtitle: "Construyendo la próxima generación de infraestructura financiera. Posiciones abiertas en producto, ingeniería y go-to-market.",
        content: [
          "IBYLIT está contratando en gestión de producto, ingeniería de plataforma, arquitectura de soluciones y go-to-market enterprise.",
          "Operamos como un equipo distribuido con un fuerte sesgo hacia ownership, profundidad técnica y engagement directo con clientes corporativos.",
          "Si quieres dar forma a la capa de infraestructura financiera sobre la que correrán bancos y corporativos, escríbenos a soporte@ibylit.com.",
        ],
      },
    },
  },
  heroV2: {
    eyebrow: "Infraestructura financiera y transformación digital",
    headline: "Infraestructura financiera para transformar la operación de bancos y empresas",
    sub: "IBYLIT diseña, implementa, integra y opera soluciones financieras digitales sobre la infraestructura existente de cada organización.",
    cta: "Solicitar Demo",
    ctaPrimary: "Conocer nuestras capacidades",
    ctaSecondary: "Solicitar una evaluación",
    counter: "01 / 09",
    badge: "EN VIVO",
    proofValidated: { label: "Titularidad", value: "Confirmada" },
    proofSettlement: { label: "Liquidación", value: "SINPE Directo" },
    orchestrator: {
      folioLabel: "FOLIO",
      folio: "CRC-882-9011",
      amountLabel: "Valor nominal factura",
      amount: "₡14.850.200",
      acceptedByPayer: "Aceptada por el pagador",
      rows: {
        advance: { label: "Anticipo de facturas", valuePrefix: "Tasa de descuento", valueNumber: "1,15%", tag: "Liquidado T+0" },
        auction: { label: "Subasta de fondeo", valuePrefix: "", valueNumber: "4", valueSuffix: "ofertas activas", tag: "Mejor tasa" },
        registry: { label: "Anotación en cuenta", value: "GAUDI-9928-IBY", tag: "Custodia activa" },
      },
    },
  },
  productLive: {
    eyebrow: "Producto en vivo",
    title: "Control operativo en tiempo real, sin tickets ni planillas.",
    sub: "Visibilidad punta a punta de cada activo, cada puja y cada liquidación — con trazabilidad y reglas auditables al instante.",
    browserUrl: "",
    chips: [
      { label: "Estados en vivo", desc: "Cada operación, en tiempo real" },
      { label: "Control operativo", desc: "Reglas y flujos auditables" },
      { label: "Trazabilidad end-to-end", desc: "Originación → ejecución → liquidación" },
    ],
    alt: "Consola IBYLIT — Subastas en vivo",
  },
  problemV2: {
    eyebrow: "Qué resolvemos",
    title: "La liquidez está fragmentada. La infraestructura para conectarla no existe.",
    intro: "Empresas, bancos, ERPs y fondeadores operan en silos desconectados. Resultado: capital atrapado, procesos manuales, sin trazabilidad.",
    items: [
      "Fragmentación entre ERPs, bancos y fondeadores",
      "Procesos manuales sin trazabilidad",
      "Liquidez atrapada en activos sin estructurar",
      "Falta de un orquestador neutral",
    ],
  },
  productsShowcase: {
    eyebrow: "Productos",
    title: "Multi-producto. Una infraestructura.",
    sub: "Cada producto estructura una pieza del flujo financiero. Juntos forman la columna operacional, extensible a nuevos instrumentos según evoluciona el negocio.",
    items: [
      {
        key: "factoring",
        name: "Factoring",
        solves: "Las empresas venden sus cuentas por cobrar a un tercero (factor) para obtener liquidez inmediata, transfiriendo el derecho de cobro del crédito. Puede estructurarse con o sin recurso, según quién asuma el riesgo de incobrabilidad.",
        how: ["Carga del activo", "Validación y estructuración", "Asignación al fondeador"],
        diff: ["Activos verificados", "Listo para subasta", "Trazabilidad completa"],
        processing: "Los documentos son estructurados y validados antes de su incorporación al flujo de negociación.",
      },
      {
        key: "confirming",
        name: "Confirming",
        solves: "Las corporaciones quieren extender plazos de pago mientras sus proveedores cobran anticipado.",
        how: ["Aprobación de factura", "Oferta al proveedor", "Settlement con fondeador"],
        diff: ["White-label", "Multi-fondeador", "Listo para supply chain"],
        processing: "Los documentos son estructurados y validados antes de su incorporación al flujo de negociación.",
      },
      {
        key: "payments",
        name: "Pagos B2B",
        solves: "Los flujos de pago entre empresas no tienen visibilidad, estructura ni conciliación.",
        how: ["Orquestación del flujo", "Ruteo por rieles bancarios", "Conciliación automática"],
        diff: ["Agnóstico al riel", "Trazable", "Embebible"],
        processing: "Los documentos son estructurados y validados antes de ser ruteados para ejecución.",
      },
      {
        key: "auctions",
        name: "Subastas",
        solves: "El descubrimiento de precio del activo es opaco. Los fondeadores compiten sin visibilidad estructurada.",
        how: ["Listado del activo", "Subasta competitiva", "Adjudicación verificable"],
        diff: ["Descubrimiento de precio", "Verificable", "Multi-fondeador"],
        processing: "Los documentos son estructurados y validados antes de su incorporación al flujo de negociación.",
      },
      {
        key: "annotation",
        name: "Anotación",
        solves: "Los instrumentos financieros necesitan una identidad digital única durante todo su ciclo.",
        how: ["Registro digital", "Identificador único", "Metadata inmutable"],
        diff: ["Atado al lifecycle", "Auditable", "Interoperable"],
        processing: "La información procesada alimenta el registro operativo y la trazabilidad de posiciones.",
      },
      {
        key: "custody",
        name: "Custodia Operativa",
        solves: "El estado del instrumento debe resguardarse desde la originación hasta el settlement.",
        how: ["Gestión de estado", "Tracking de eventos", "Cierre del lifecycle"],
        diff: ["Solo operativa", "Registro interno", "No es custodia regulada"],
        processing: "La información procesada alimenta el registro operativo y la trazabilidad de posiciones.",
      },
    ],
    counter: "PRODUCTO",
  },
  annotationDeep: {
    eyebrow: "Anotación & Custodia Operativa",
    title: "El lifecycle completo del instrumento, registrado y trazado.",
    sub: "Desde la originación hasta el cierre, cada evento queda registrado. Registro interno — no es custodia regulada.",
    timeline: ["Originación", "Anotación", "Cesión", "Fondeo", "Settlement", "Cierre"],
    blocks: [
      {
        title: "Anotación",
        body: "Registro digital del instrumento con identificador único y metadata operativa. Cada instrumento existe como un registro verificable a lo largo de su ciclo.",
      },
      {
        title: "Custodia Operativa",
        body: "Resguardo lógico del estado del instrumento durante originación, cesión, fondeo y settlement. Función interna de plataforma, no custodia regulada.",
      },
    ],
    disclaimer: "Registro interno — no constituye custodia regulada.",
  },
  ecosystemFlow: {
    eyebrow: "Ecosistema conectado",
    title: "Todos los actores del ecosistema.",
    sub: "Ibylit conecta actores, regula la operación y permite ejecutar múltiples estructuras financieras —factoring, confirming, subastas y pagos— sobre una misma infraestructura.",
    reinforcement: "Cada actor interactúa bajo reglas claras, con visibilidad completa del proceso y sin fricción entre sistemas.",
    hub: { name: "IBYLIT", role: "Orquestador neutral", does: "Coordina el flujo entre todos los participantes bajo reglas únicas.", sees: "Estado completo de cada operación punta a punta." },
    inputs: { title: "Qué entra al flujo", items: ["Activos desde ERPs", "Facturas aprobadas", "Capital disponible"] },
    outputs: { title: "Qué sale del flujo", items: ["Liquidez estructurada", "Settlement trazable", "Flujos conciliados"] },
    actors: [
      { key: "payer", name: "Pagador", role: "Empresa que paga obligaciones", does: "Origina la deuda y aprueba facturas para pago programado.", sees: "Estado de pagos, proveedores y calendario de obligaciones." },
      { key: "supplier", name: "Proveedor", role: "Empresa que cobra", does: "Cede facturas para anticipo de liquidez.", sees: "Liquidez disponible, fechas de cobro y ofertas de descuento." },
      { key: "funder", name: "Fondeador", role: "Aporta capital", does: "Selecciona oportunidades y financia operaciones.", sees: "Portfolio activo, rendimiento y exposición por contraparte." },
      { key: "bank", name: "Banco", role: "Provee rieles y core", does: "Ejecuta movimientos y conciliación bancaria.", sees: "Flujos transaccionales y conciliación en tiempo real." },
      { key: "factor", name: "Factor", role: "Estructura descuento", does: "Define condiciones comerciales y tasas de operación.", sees: "Cartera activa, márgenes y riesgo agregado." },
      { key: "settler", name: "Liquidador", role: "Ejecuta cierres", does: "Coordina settlement y fechas de liquidación.", sees: "Calendario operativo y estado de cierres." },
      { key: "custodian", name: "Custodio", role: "Resguarda instrumentos", does: "Registra titularidad y movimientos del instrumento.", sees: "Inventario, titularidad y trazabilidad histórica." },
      { key: "erp", name: "ERP / Sistema contable", role: "Sincroniza datos", does: "Emite documentos y sincroniza información financiera.", sees: "Reconciliación automática y reportería contable." },
    ],
    detailLabels: { role: "Rol", does: "Qué hace", sees: "Qué ve" },
    nodes: { hub: "IBYLIT", banks: "Bancos", erps: "ERPs", companies: "Empresas", funders: "Fondeadores" },
    processingChip: "Procesamiento inteligente",
    processingTooltip: "Ibylit transforma documentos comerciales en datos estructurados mediante extracción, normalización y validación automatizada, reduciendo errores manuales y mejorando la consistencia operativa.",
  },
  byActor: {
    eyebrow: "Por Actor",
    title: "Qué obtiene cada participante.",
    sub: "Misma infraestructura. Distinto valor según tu rol.",
    actors: [
      {
        key: "banks",
        name: "Bancos",
        valueLine: "Lanzan productos financieros sin reemplazar su core.",
        benefits: [
          "Factoring, confirming y subastas bajo su marca",
          "Integración no invasiva con su core bancario",
          "Time-to-market de años a meses",
        ],
      },
      {
        key: "corporates",
        name: "Corporativos",
        valueLine: "Estructuran programas con proveedores y optimizan capital de trabajo.",
        benefits: [
          "Programas de confirming punta a punta",
          "Trazabilidad punto a punto de cada flujo",
          "Optimización de capital de trabajo",
        ],
      },
      {
        key: "smes",
        name: "PYMEs",
        valueLine: "Acceden a liquidez sobre sus facturas, sin fricción operativa.",
        benefits: [
          "Conexión a una red de fondeadores verificados",
          "Proceso digital y transparente",
          "Sin inversión tecnológica requerida",
        ],
      },
      {
        key: "funders",
        name: "Fondeadores",
        valueLine: "Acceso estructurado a oportunidades verificadas.",
        benefits: [
          "Oportunidades verificadas, estructuradas para análisis",
          "Subastas verificables y settlement trazable",
          "Custodia operativa de los instrumentos asignados",
        ],
        disclaimer: "Registro interno — no constituye custodia regulada.",
      },
    ],
    cta: "Hablar con el equipo",
  },
  architectureLayers: {
    eyebrow: "Arquitectura",
    title: "Modular por diseño. Integrable por defecto.",
    sub: "Cinco capas que componen la infraestructura. Úsalas juntas o embébelas individualmente.",
    layers: [
      { num: "01", title: "Cloud-Native APIs", body: "Integración directa vía APIs REST y webhooks. Sin SDKs, sin instalaciones, sin mantenimiento de librerías." },
      { num: "02", title: "Orquestación de Flujos", body: "Coordina el lifecycle entre actores sin ejecutar operaciones financieras." },
      { num: "03", title: "Anotación & Lifecycle", body: "Registro digital de instrumentos con trazabilidad completa de eventos." },
      { num: "04", title: "Integraciones", body: "Conectores para ERPs, cores bancarios, contabilidad y autoridades fiscales." },
      { num: "05", title: "Seguridad & Cumplimiento", body: "Cifrado TLS 1.3 en tránsito y en reposo, control de acceso a nivel de fila y trazabilidad completa. Diseñado contra los marcos de control SOC 2 e ISO 27001." },
    ],
  },
  intelligence: {
    eyebrow: "Inteligencia",
    body: "Inteligencia aplicada a decisiones operativas — scoring, ruteo y priorización. Complementa el criterio humano, nunca lo reemplaza.",
  },
  solutionOverview: {
    eyebrow: "QUÉ ES IBYLIT",
    title: "No reemplazamos tu core. Lo orquestamos.",
    intro: "Ibylit es una capa cloud de orquestación financiera B2B white-label. No sustituye tus sistemas core: los conecta, coordina actores y mueve flujos de liquidez en tiempo real.",
    blocks: [
      { label: "Lo que es", body: "Capa de orquestación entre bancos, corporaciones, fondeadores y PYMEs sobre tu infraestructura existente." },
      { label: "Lo que no es", body: "No es un core bancario, no es un ERP, no es un SaaS vertical aislado." },
      { label: "Cómo se posiciona", body: "Integra datos, actores y ciclo de vida completo: originación → decisión → ejecución → liquidación → custodia." },
    ],
  },
  intelligenceLayer: {
    eyebrow: "CAPA DE PROCESAMIENTO",
    aiBadge: "Impulsado por AI",
    title: "Procesamiento inteligente integrado a la operación",
    intro: "La plataforma incorpora una capa de procesamiento, impulsada por AI, que convierte información no estructurada en datos operables, verificables y trazables dentro del flujo financiero.",
    mockup: {
      title: "Subir Facturas",
      stepActive: "Validar facturas",
      stepNext: "Registrar facturas",
      productLabel: "Seleccione el producto para continuar",
      products: ["Confirming", "Pago Proveedor", "Factoring", "Subastas"],
      template: "Descargar plantilla",
      dropTitle: "Arrastra tus facturas aquí",
      dropHint: "o haz clic en el botón",
      dropCta: "Seleccionar facturas con AI",
      formats: "Tipos aceptados: .XML, .XLS, .XLSX, .PDF, .PNG, .JPG",
      validate: "Validar facturas",
      next: "Continuar al paso 2",
      caption: "Vista ilustrativa — la pantalla real puede variar según el despliegue.",
    },
    flow: {
      label: "Flujo operativo",
      steps: ["Entrada", "Procesamiento inteligente", "Estructuración", "Ejecución", "Settlement"],
      highlight: 1,
    },
    capabilities: [
      { title: "Estructuración de información", body: "Transformación de documentos en datos organizados y utilizables dentro del flujo operativo." },
      { title: "Validación de consistencia", body: "Verificación de campos clave y detección de inconsistencias antes de avanzar en el proceso." },
      { title: "Reducción de intervención manual", body: "Disminución de procesos manuales repetitivos, reduciendo errores y fricción operativa." },
      { title: "Integración al flujo financiero", body: "Los datos estructurados se integran directamente en los procesos de subasta, adjudicación y liquidación." },
    ],
    trust: "Los procesos están diseñados para producir resultados consistentes y verificables, priorizando la calidad de datos y la trazabilidad operativa.",
    disclaimer: "La validación documental y el procesamiento automatizado apoyan la calidad y consistencia de la información, pero no sustituyen validaciones legales o regulatorias realizadas por las entidades correspondientes.",
    dashboardMockup: {
      title: "Dashboard general",
      clientPlaceholder: "Cliente",
      caption: "Vista ilustrativa. Datos demostrativos, no representan información real.",
      kpis: {
        activeTotal: "Total vigente",
        overdueTotal: "Total vencido",
        invoicesCount: "Cantidad de operaciones",
        financedCollections: "Cobros con financiamiento",
        unfinancedCollections: "Cobros sin financiamiento",
        accumulatedReceivable: "Acumulado por cobrar",
      },
      projection: {
        title: "Proyección de cobros del mes",
        today: "Por cobrar hoy",
        week: "Por cobrar esta semana",
        month: "Por cobrar este mes",
        unitA: "Unidad A",
        unitB: "Unidad B",
        r1: "S1",
        r2: "S2",
        r3: "S3",
        r4: "S4",
        r5: "S5",
        r6: "S6",
      },
    },
  },
  finalCta: {
    eyebrow: "Comenzar",
    title: "Construye tu infraestructura financiera con IBYLIT.",
    sub: "30 minutos. Tu equipo y el nuestro. Sin compromiso.",
    cta: "Solicitar Demo",
    contact: "soporte@ibylit.com",
  },
  demoForm: {
    eyebrow: "[ SOLICITAR DEMO ]",
    title: "Conversemos sobre tu infraestructura de liquidez.",
    subtitle: "30 minutos con nuestro equipo. Sin compromiso.",
    fields: {
      name: "Nombre completo",
      email: "Correo corporativo",
      company: "Empresa",
      message: "Cuéntanos brevemente tu caso de uso (opcional)",
    },
    placeholders: {
      name: "Juan Pérez",
      email: "tu@empresa.com",
      company: "Empresa S.A.",
      message: "Estamos explorando infraestructura de factoring para nuestro marketplace…",
    },
    submit: "Enviar solicitud",
    sending: "Enviando…",
    success: "Recibimos tu solicitud. Te contactaremos en menos de 24h.",
    error: "Hubo un problema. Intenta de nuevo o escríbenos a soporte@ibylit.com.",
    rateLimited: "Ya recibimos tu solicitud con este correo. Por seguridad, espera unos 20 minutos antes de enviar otra, o escríbenos directamente a soporte@ibylit.com.",
    captchaRequired: "Por favor completa la verificación antes de enviar.",
    consent: "Al enviar aceptas ser contactado por el equipo de IBYLIT.",
    close: "Cerrar",
  },
  fiscalInfra: {
    eyebrow: "INTEGRACIONES FISCALES",
    title: "Infraestructura fiscal conectada en la región",
    subtitle: "Operación sobre sistemas oficiales, en tiempo real",
    description:
      "Ibylit conecta directamente con los sistemas fiscales de cada país para operar sobre información oficial, validada y trazable. Hoy, la plataforma ya se encuentra integrada con el Ministerio de Hacienda de Costa Rica y ARCA Argentina, construyendo operaciones financieras sobre una base regulatoria real.",
    chips: ["Ministerio de Hacienda · Costa Rica", "ARCA · Argentina"],
    countries: [
      {
        key: "ar",
        code: "AR",
        name: "Argentina",
        subtitle: "Facturas como instrumentos negociables",
        paragraph:
          "Ibylit opera sobre el régimen de Factura Electrónica de Crédito (FCE), donde cada comprobante sigue un flujo regulado.",
        items: [
          "Validación del estado del comprobante",
          "Confirmación de aceptación o rechazo",
          "Determinación de saldo negociable",
          "Identificación de elegibilidad para mercado",
        ],
      },
      {
        key: "cr",
        code: "CR",
        name: "Costa Rica",
        subtitle: "Validación sobre comprobantes electrónicos",
        paragraph:
          "Ibylit consulta el estado de los comprobantes electrónicos directamente desde el sistema de Hacienda mediante su clave única.",
        items: [
          "Verificación del estado de recepción",
          "Confirmación de existencia del documento",
          "Trazabilidad desde el origen",
        ],
      },
    ],
    bridge: {
      title: "De la validación a la operación",
      intro:
        "Ibylit transforma documentos electrónicos en activos operables. Una vez validados, estos documentos pueden:",
      capabilities: [
        "Anotarse y mantenerse trazables",
        "Custodiarse a lo largo de su ciclo de vida",
        "Participar en procesos financieros regulados",
      ],
      processesLabel: "Procesos habilitados",
      processes: [
        "Factoring",
        "Confirming",
        "Subastas de liquidez",
        "Operaciones OTC",
        "Compras y ventas privadas",
        "Transferencias de titularidad",
      ],
    },
    closing:
      "Ibylit convierte información proveniente de sistemas fiscales en una base confiable para anotar, custodiar y negociar activos financieros en la región.",
  },
  regulatory: {
    eyebrow: "MARCO REGULATORIO",
    title: "Infraestructura construida sobre marcos legales existentes",
    intro:
      "Ibylit permite estructurar operaciones de liquidez digital sobre activos que ya existen y operan bajo marcos legales vigentes en distintas jurisdicciones. La plataforma no crea nuevos instrumentos regulatorios ni reemplaza intermediarios financieros: organiza y conecta procesos sobre estructuras legales existentes.",
    indicators: [
      "Marco legal existente",
      "Activo operando",
      "Infraestructura conectiva",
    ],
    jurisdictions: [
      {
        key: "ar",
        name: "Argentina",
        intro: "Operaciones estructuradas sobre activos bajo:",
        laws: [
          {
            code: "Ley 27.440",
            name: "Financiamiento Productivo · Factura de Crédito Electrónica (FCE)",
            flow: {
              label: "Flujo operativo habilitado",
              intro: "Sobre este marco, Ibylit estructura el ciclo completo de la Factura de Crédito Electrónica como instrumento negociable:",
              items: [
                "Emisión de la Factura de Crédito Electrónica conforme a normativa vigente",
                "Aceptación formal del instrumento por parte del pagador (expresa o por vencimiento del plazo legal)",
                "Registro del instrumento aceptado en agente de depósito colectivo",
                "Estructuración del financiamiento mediante confirming, anticipos o subastas reguladas",
                "Cesión y transferencia de titularidad dentro del esquema regulado",
                "Cobranza y liquidación al titular registrado al vencimiento",
              ],
            },
          },
        ],
        note: "Este marco habilita la negociación estructurada de la Factura de Crédito Electrónica como instrumento financiero dentro de esquemas regulados.",
      },
      {
        key: "cr",
        name: "Costa Rica",
        intro: "Operaciones estructuradas sobre activos que se enmarcan en:",
        laws: [
          {
            code: "Ley 9691",
            name: "Factoreo electrónico y cesión de derechos de crédito",
            flow: {
              label: "Flujo operativo habilitado",
              intro: "Sobre este marco, Ibylit estructura el ciclo completo del crédito:",
              items: [
                "Validación del comprobante electrónico ante el Ministerio de Hacienda",
                "Identificación del derecho de cobro como activo financiero",
                "Formalización de la cesión mediante contratos de factoreo",
                "Notificación al pagador conforme a normativa vigente",
                "Registro y trazabilidad de la operación en plataforma electrónica",
                "Gestión del crédito por parte del factor",
              ],
            },
          },
          {
            code: "Ley 10039",
            name: "Carácter ejecutivo de la factura electrónica y posible anotación en cuenta como valor negociable",
            flow: {
              label: "Extensión a anotación en cuenta",
              intro: "Cuando el modelo lo requiere, el flujo puede extenderse hacia esquemas de mercado de valores:",
              items: [
                "Solicitud de registro ante una central de valores",
                "Confirmación de aceptación del pagador (explícita o tácita dentro de plazo legal)",
                "Anotación del instrumento como valor",
                "Custodia y registro de titularidad",
                "Habilitación para circulación y negociación",
              ],
            },
          },
        ],
        note: "Estos marcos permiten la cesión, gestión y financiamiento de cuentas por cobrar, así como su estructuración en esquemas de anotación y trazabilidad cuando se utilizan infraestructuras del mercado de valores.",
      },
    ],
    role: {
      title: "El rol de Ibylit",
      intro: "Ibylit opera como una capa de infraestructura que:",
      items: [
        "organiza información",
        "estructura procesos",
        "conecta participantes",
      ],
      closing: "sin asumir el rol de entidad financiera regulada.",
    },
    riskReduction: {
      title: "Diseñado para reducir fricción operativa y riesgo percibido",
      items: [
        "Estructuración sobre activos existentes",
        "Trazabilidad completa de operaciones",
        "Evidencia verificable en cada etapa",
        "Integración con actores regulados",
        "Separación clara de roles",
        "Reducción de fricción operativa",
      ],
    },
    disclaimer:
      "Ibylit no es una entidad financiera, no intermedia recursos ni actúa como custodio regulado. La plataforma facilita la estructuración, trazabilidad y operación del ciclo completo de los activos, desde su validación hasta su utilización en procesos de financiamiento, sobre marcos legales existentes.",
  },
  caseStudies: {
    eyebrow: "Casos de éxito",
    title: "Infraestructura real. Operación conectada. Liquidez en movimiento.",
    subtitle:
      "Implementaciones en producción donde Ibylit actúa como capa de orquestación, integrando core bancario, actores del mercado y sistemas externos en flujos financieros en tiempo real.",
    quote: "No reemplazamos sistemas. Los conectamos para operar en tiempo real.",
    cases: [
      {
        flag: "🇨🇷",
        label: "Banco 1 · Costa Rica",
        resolvedTitle: "Qué se resolvió",
        resolved:
          "Integración del ecosistema de confirming sin sustituir el core bancario.",
        connectedTitle: "Qué se conectó",
        connected: [
          "Core bancario (riesgo, contabilidad, operación)",
          "Transferencias locales",
          "Interoperabilidad con otros bancos",
          "Conectividad internacional",
        ],
        achievedTitle: "Qué se logró",
        achieved:
          "Flujo continuo entre originación, evaluación y ejecución, con información unificada en tiempo real.",
      },
      {
        flag: "🇨🇷",
        label: "Banco 2 · Costa Rica",
        resolvedTitle: "Qué se resolvió",
        resolved:
          "Despliegue de productos de pagos y financiamiento de cadenas de valor, integrando operación interna y actores externos en un flujo digital unificado.",
        implementedTitle: "Qué se implementó",
        implemented: [
          "Soluciones de pagos corporativos",
          "Financiamiento de proveedores",
          "Modelos con recurso y sin recurso",
          "Automatización de flujos financieros",
        ],
        connectedTitle: "Qué se conectó",
        connected: [
          "Sistemas internos del banco",
          "Integración con ERP de pagadores y proveedores",
          "Infraestructura de pagos y transferencias",
          "Interacción con otras entidades financieras",
        ],
        achievedTitle: "Qué se logró",
        achieved:
          "Operación end-to-end automatizada, conectando empresas y banco en un mismo flujo de información y ejecución.",
      },
      {
        flag: "🇦🇷",
        label: "Plataforma distribuidora · Argentina",
        resolvedTitle: "Qué se resolvió",
        resolved:
          "Construcción completa de infraestructura de liquidez basada en instrumentos.",
        connectedTitle: "Qué se implementó",
        connected: [
          "Anotación digital",
          "Custodia legal",
          "Negociación en mercado primario y secundario",
          "Liquidación DVP",
          "Múltiples fondeadores en competencia",
        ],
        achievedTitle: "Qué se logró",
        achieved:
          "Ecosistema digital donde la liquidez se genera mediante interacción competitiva entre actores.",
      },
    ],
    capabilitiesTitle: "Capacidades comunes en todos los casos",
    capabilities: [
      "Integración con sistemas existentes (sin reemplazo)",
      "Orquestación de múltiples actores",
      "Flujos financieros en tiempo real",
      "Automatización operativa y contable",
      "Trazabilidad completa end-to-end",
      "Arquitectura cloud escalable",
    ],
    resultLabel: "Resultado",
    result:
      "Procesos fragmentados convertidos en flujos financieros conectados, automatizados y escalables.",
    cta: "Ver cómo funciona",
  },
  calculator: {
    eyebrow: "Cotizador Inteligente",
    title: "Simula tu anticipo de facturas",
    subtitle:
      "Descubre cuánto puedes recibir hoy por tus facturas pendientes con nuestra calculadora de factoring",
    inputTitle: "Datos de la factura",
    inputSubtitle: "Ingresa los detalles para cotizar",
    amountLabel: "Monto de la factura",
    daysLabel: "Días hasta vencimiento",
    daysHint: "Entre 1 y 365 días",
    calculating: "Calculando con IA...",
    calculate: "Cotizar con IA",
    resultTitle: "Resultado",
    resultSubtitle: "Tu cotización personalizada",
    rateApplied: "Tasa aplicada",
    receiveToday: "Monto a recibir hoy",
    advanceCost: "Costo del anticipo",
    recalc: "Recotizar con nueva tasa",
    requestDemo: "Solicitar Demo",
    emptyTitle: "Ingresa los datos",
    emptyHint: 'Y presiona "Cotizar con IA" para ver tu resultado',
    disclaimer:
      "* Esta simulación es ilustrativa. Las tasas reales dependen del perfil de riesgo, el pagador y las condiciones de mercado. Contacte a nuestro equipo para una cotización personalizada.",
  },
  mockups: {
    auction: {
      path: "/ Facturas",
      kpis: { active: "Activas", closed: "Cerradas", volume: "Volumen" },
      live: "LIVE",
      amount: "Monto",
      bids: "Ofertas",
      status: {
        auction: "En subasta",
        approved: "Aprobada",
        cancelled: "Cancelada",
      },
      showing: "Mostrando",
      of: "de",
      invoices: "facturas",
      hideCards: "Ocultar tarjetas",
      payer: "PAGADOR",
      negotiableAmount: "Monto referencial",
      operator: "Operador",
      days: "días",
      rate: "Tasa",
      offer: "Oferta",
      noOffers: "Sin ofertas",
      illustrative: "Datos ilustrativos",
      hideDetails: "Ocultar detalles",
      seeOffers: "Ver ofertas",
      invoiceHeader: "FACTURA - SUBASTAS",
      term: "Plazo",
      dueDate: "Vencimiento",
      acceptedRate: "Tasa aceptada",
      bestOffer: "Mejor oferta",
      provider: "Proveedor",
      providerName: "Operador Demo",
    },
    cancellation: {
      title: "Cancelación",
      path: "/ Facturas en gestión",
      donut: { active: "Vigentes", overdue: "Vencidas", recovery: "Recupero" },
      search: "Buscar por proveedor o factura…",
      filters: "Filtros",
      export: "Exportar",
      cols: {
        invoice: "Factura",
        supplier: "Proveedor",
        payer: "Pagador",
        amount: "Monto",
        status: "Estado",
      },
      status: { active: "Vigente", overdue: "Vencida" },
    },
  },
  eyebrows: {
    ecosystem: "Ecosistema",
    summary: "Resumen",
    security: "Seguridad",
    platform: "La plataforma",
    products: "Productos",
    howItWorks: "Cómo funciona",
    differentiator: "Diferencial",
  },
  architectureExtra: {
    platformLabel: "Plataforma White-Label",
    coreLabel: "Core Financiero en la Nube",
    coreDesc: "Factoring • Confirming • Pagos B2B • Subastas",
  },
  governanceExtra: {
    message: "Tecnología diseñada para escalar",
    messageHighlight: "con su negocio",
  },
  operationalFlow: {
    eyebrow: "FLUJO OPERATIVO",
    title: "Flujo operativo end-to-end",
    intro: "Del ingreso de la factura al cierre registral. Cada paso refleja el comportamiento real del sistema.",
    nav: { prev: "Anterior", next: "Siguiente", step: "Paso" },
    steps: [
      {
        num: "01",
        title: "Entrada",
        description: "Ingreso de facturas desde ERP, XML o carga manual.",
        subItems: ["ERP", "XML / UBL", "Carga manual"],
        visualKey: "entrada",
      },
      {
        num: "02",
        title: "Procesamiento inteligente",
        description: "Validación, normalización y enriquecimiento automático de cada documento.",
        subItems: ["Validación", "Normalización", "Enriquecimiento", "Trazabilidad"],
        visualKey: "procesamiento",
        ctaLabel: "Ver detalle del procesamiento ↓",
      },
      {
        num: "03",
        title: "Estructuración",
        description: "Definición de condiciones financieras y aprobaciones según el tipo de operación.",
        subItems: ["Confirming", "Anticipos", "Reglas de aprobación"],
        visualKey: "estructuracion",
      },
      {
        num: "04",
        title: "Ejecución",
        description: "Ejecución de desembolsos, DVP o liquidación de fondos vía integración bancaria o mercado.",
        subItems: ["Desembolsos", "DVP", "Liquidación"],
        visualKey: "ejecucion",
      },
      {
        num: "05",
        title: "Settlement",
        subtitle: "Cierre operativo y registral",
        description: "Registro final con documentación legal, posiciones y conciliación contable.",
        subItems: ["Documentación", "Posiciones", "Saldos", "Asiento contable", "Evidencia legal"],
        visualKey: "settlement",
      },
    ],
    visuals: {
      panelLabels: {
        entrada: "app.ibylit.com / subir-facturas",
        procesamiento: "motor / procesamiento",
        estructuracion: "operación / estructurar",
        ejecucion: "ejecución / TX-2847",
        settlement: "ledger / cierre operativo",
      },
      entrada: {
        dropHint: "Arrastrá facturas o conectá tu ERP",
        formats: "XML · UBL · CSV · PDF",
        fileProgress: "FAC-2847.xml · 67%",
        ready: "3 facturas recibidas · listas para validar",
      },
      procesamiento: {
        rows: [
          "Validación estructural",
          "Normalización de campos",
          "Enriquecimiento (RUT, score, sector)",
          "Trazabilidad y custodia",
        ],
        states: { ok: "OK", run: "EN CURSO", wait: "EN COLA" },
      },
      estructuracion: {
        tabs: ["Confirming", "Anticipos", "Aprobaciones"],
        plazo: "Plazo",
        plazoValue: "30 / 60 / 90 días",
        tasa: "Tasa EA",
        tasaValue: "8.5%",
        flowLabel: "Flujo de aprobación",
        nodes: ["Originador", "Comité L1", "Tesorería L2"],
      },
      ejecucion: {
        nodes: ["Originador", "IBYLIT", "Banco"],
        monto: "Monto",
        tipo: "Tipo",
        estado: "Estado",
      },
      settlement: {
        asiento: "Asiento",
        rows: [
          { k: "Posición", v: "+ 48,200" },
          { k: "Saldo", v: "1,247,800" },
          { k: "Cuenta", v: "1.1.05.001" },
        ],
        stamp: "✓ REGISTRADO · AUDITABLE",
      },
    },
  },
  common: {
    disclaimer: "Disclaimer",
  },
  ctaSection: {
    titleA: "Lance su plataforma de liquidez digital.",
    titleB: "Bajo su propia marca.",
    button: "Solicitar Demo",
    footnote: "Contáctenos para conocer cómo Ibylit puede potenciar su negocio",
  },
  heroSectionLegacy: {
    headlineLine1: "Plataforma de factoring y confirming",
    headlineLine2: "B2B White-Label",
    subheadline: "Potenciamos a las instituciones financieras para ofrecer soluciones de liquidez digital bajo su propia marca.",
    cta: "Solicitar demo",
  },
  problemSegments: {
    badge: "¿Para quién?",
    title: "Diseñada para instituciones que buscan escalar",
    subtitle: "Ibylit permite a instituciones financieras y grandes corporaciones ofrecer soluciones de liquidez digital bajo su propia marca",
    items: [
      { title: "Bancos", description: "Ofrezca productos de liquidez digital a sus clientes corporativos bajo su propia marca." },
      { title: "Bolsas de Valores", description: "Habilite un marketplace para la negociación de facturas y documentos comerciales." },
      { title: "Brokers & Fintechs", description: "Lance productos de factoring y confirming sin desarrollar tecnología desde cero." },
      { title: "Corporaciones", description: "Gestione el financiamiento de su cadena de proveedores de forma eficiente." },
    ],
  },
  architectureActors: {
    title: "Un ecosistema que conecta a todos los participantes",
    closing: "Ibylit conecta proveedores, pagadores y fondeadores en una plataforma unificada para la gestión de liquidez digital.",
    items: [
      { title: "Proveedores", description: "Acceso a financiamiento de facturas • Autogestión de documentos • Visibilidad de pagos" },
      { title: "Pagadores / Corporaciones", description: "Gestión de cuentas por pagar • Programas de confirming • Optimización de capital de trabajo" },
      { title: "Fondeadores / Inversionistas", description: "Acceso a marketplace • Compra de facturas • Dashboard de inversiones" },
    ],
    securityFeatures: [
      "Encriptación avanzada",
      "Control de accesos",
      "Trazabilidad completa",
      "Multi-usuario y multi-perfil",
      "Arquitectura escalable",
    ],
  },
  solutionCapabilities: {
    title: "Tecnología diseñada para escalar",
    subtitle: "Arquitectura cloud-native con las capacidades que necesitan las instituciones financieras",
    items: [
      { title: "Adaptación full white-label", description: "Personalización impulsando lealtad a la marca y valor de por vida del cliente." },
      { title: "Core de productos en la nube", description: "Multiproducto, multimoneda y multipaís." },
      { title: "Seguridad", description: "Seguridad en la nube con encriptación avanzada, resiliencia operativa y arquitectura de microservicios escalable." },
      { title: "Marketplace por producto", description: "Espacio de encuentro para la negociación de documentos comerciales y financieros." },
      { title: "Autogestión", description: "Suite de autogestión para proveedores, pagadores, inversionistas y entidades financieras, multi-usuario y multi-perfil." },
      { title: "Integración por API", description: "Interconexión con sus sistemas internos que le permite escalar procesos y automatizar tareas." },
      { title: "Notificaciones", description: "Alertas en tiempo real para mantener informados a todos los participantes del ecosistema." },
      { title: "Dashboard de datos", description: "Visualización y análisis de información en tiempo real para la toma de decisiones." },
    ],
  },
  useCaseProducts: {
    title: "Productos financieros en la nube",
    subtitle: "Core multiproducto para gestionar integralmente operaciones de liquidez digital",
    items: [
      { title: "Reverse Factoring / Confirming", description: "Plataforma en la nube para la administración de cuentas por pagar y financiamiento de proveedores en cadenas de valor." },
      { title: "Factoring Digital", description: "Plataforma en la nube que simplifica la operación del financiamiento de cuentas por cobrar comerciales conectando proveedores, pagadores y entidades financieras." },
      { title: "Pagos B2B", description: "Plataforma en la nube que optimiza las condiciones y la gestión de pago de facturas electrónicas en las cadenas de suministro." },
      { title: "Subastas Electrónicas", description: "Plataforma en la nube donde proveedores, pagadores y fondeadores acceden a un marketplace 100% online para la negociación de sus facturas y documentos." },
    ],
  },
  executiveDeck: {
    title: "¿Por qué Ibylit?",
    prev: "Anterior",
    next: "Siguiente",
    slides: [
      {
        badge: "White-Label",
        title: "Suite financiero completo, bajo su propia marca",
        bullets: [
          "Factoring, Confirming, Pagos B2B y Subastas",
          "Multi-producto, multi-moneda, multi-país",
          "Front, middle y back office integrado",
          "Personalización total de marca",
        ],
        message: "Lance productos de liquidez digital sin desarrollar tecnología desde cero.",
      },
      {
        badge: "Tecnología",
        title: "Arquitectura cloud-native de nivel empresarial",
        bullets: [
          "Encriptación avanzada de datos",
          "Resiliencia operativa",
          "Microservicios escalables",
          "Integración por API",
          "Multi-usuario y multi-perfil",
        ],
        message: "Seguridad y escalabilidad para instituciones financieras.",
      },
      {
        badge: "Ecosistema",
        title: "Conecta proveedores, pagadores y fondeadores",
        bullets: [],
        message: "Un marketplace para la negociación de documentos comerciales y financieros.",
      },
    ],
  },
  cookieConsent: {
    title: "Su privacidad nos importa",
    description:
      "Utilizamos cookies esenciales para operar este sitio (tema, idioma, protección anti-bot). Con su consentimiento, también podríamos usar cookies de analítica y marketing para mejorar el servicio. Puede cambiar su elección en cualquier momento.",
    cookiePolicy: "Política de Cookies",
    privacyPolicy: "Política de Privacidad",
    actions: {
      accept: "Aceptar todo",
      reject: "Rechazar no esenciales",
      configure: "Configurar",
      save: "Guardar preferencias",
      preferences: "Preferencias de cookies",
    },
    categories: {
      essential: {
        title: "Esenciales",
        desc: "Necesarias para la seguridad, idioma, tema y protección anti-bot (Cloudflare Turnstile). Siempre activas.",
      },
      analytics: {
        title: "Analítica",
        desc: "Categoría preparada para entender el uso del sitio. Actualmente no se carga ningún tracker. Si en el futuro se incorpora alguno, se activará exclusivamente tras su consentimiento explícito mediante un cargador controlado (loadWhenConsented).",
      },
      marketing: {
        title: "Marketing",
        desc: "Categoría preparada para medir campañas o personalizar contenido. Actualmente no se carga ningún tracker. Si en el futuro se incorpora alguno, se activará exclusivamente tras su consentimiento explícito mediante un cargador controlado (loadWhenConsented).",
      },
    },
  },
  legal: {
    eyebrow: "Legal",
    lastUpdated: "Última actualización",
    contactNotice:
      "Para cualquier consulta sobre este documento o para ejercer sus derechos de acceso, rectificación, supresión, oposición o portabilidad, puede contactarnos en:",
    privacy: {
      title: "Política de Privacidad",
      updated: "30 de abril de 2026",
      intro:
        "IBYLIT está comprometida con la protección de sus datos personales conforme al Reglamento General de Protección de Datos de la UE (RGPD – Reglamento (UE) 2016/679) y la Ley de Protección de la Persona frente al Tratamiento de sus Datos Personales de Costa Rica (Ley N.º 8968). Esta Política describe qué datos recopilamos, por qué y cuáles son sus derechos.",
      sections: [
        {
          heading: "1. Responsable del tratamiento",
          body: [
            "IBYLIT opera el sitio ibylit.com y la plataforma asociada. Para asuntos relacionados con datos personales, el responsable puede ser contactado en soporte@ibylit.com.",
          ],
        },
        {
          heading: "2. Datos que recopilamos",
          body: [
            "Solicitudes de demo: nombre, correo corporativo, empresa y mensaje que usted envía mediante nuestros formularios.",
            "Metadatos operativos: dirección IP en formato hash, agente de usuario y marca temporal del envío, usados exclusivamente para prevención de fraude y rate limiting.",
            "Preferencias locales: idioma y tema almacenados en su navegador mediante localStorage. Esta información no se transmite a nuestros servidores.",
          ],
        },
        {
          heading: "3. Finalidades y base legal",
          body: [
            "Atender su solicitud de demo y la comunicación comercial de seguimiento (interés legítimo / consentimiento).",
            "Proteger el servicio contra abusos, ataques automatizados y fraude (interés legítimo, art. 6.1.f del RGPD).",
            "Cumplir con obligaciones legales y contractuales aplicables.",
          ],
        },
        {
          heading: "4. Comunicación de datos y encargados de tratamiento",
          body: [
            "Solo compartimos datos con encargados estrictamente necesarios para operar el servicio: infraestructura cloud (AWS), plataforma backend, proveedor de correo transaccional y Cloudflare Turnstile para protección anti-bot. Todos los proveedores operan bajo acuerdos de protección de datos adecuados.",
            "No vendemos datos personales ni los cedemos a terceros para sus propias finalidades de marketing.",
          ],
        },
        {
          heading: "5. Transferencias internacionales",
          body: [
            "Algunos de nuestros encargados se ubican fuera de Costa Rica o del Espacio Económico Europeo. Cuando esto ocurre, las transferencias se protegen mediante Cláusulas Contractuales Tipo o garantías equivalentes.",
          ],
        },
        {
          heading: "6. Conservación",
          body: [
            "Los datos de las solicitudes de demo se conservan durante el tiempo estrictamente necesario para atender su solicitud y, en su caso, la relación comercial. Los registros operativos se conservan por períodos limitados acordes con requisitos de seguridad y normativos.",
          ],
        },
        {
          heading: "7. Sus derechos",
          body: [
            "Puede ejercer sus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad escribiendo a soporte@ibylit.com.",
            "Bajo el RGPD, también tiene derecho a presentar reclamación ante la autoridad de control competente. En Costa Rica, la autoridad de control es la PRODHAB (Agencia de Protección de Datos de los Habitantes).",
          ],
        },
        {
          heading: "8. Seguridad",
          body: [
            "Aplicamos medidas técnicas y organizativas estándar de la industria: TLS 1.2/1.3 en tránsito, controles de acceso, monitoreo, rate limiting y protección contra bots. Ningún sistema es totalmente infalible, pero trabajamos de forma continua para mitigar riesgos.",
          ],
        },
        {
          heading: "9. Rol de la plataforma",
          body: [
            "IBYLIT actúa exclusivamente como proveedor tecnológico de infraestructura para la gestión, estructuración y ejecución de operaciones financieras entre terceros.",
            "IBYLIT no actúa como entidad financiera, custodio, intermediario de valores ni como titular de derechos crediticios, salvo que se indique expresamente en acuerdos contractuales específicos.",
            "La plataforma facilita la conexión entre participantes (empresas, instituciones financieras, fondeadores y otros actores del ecosistema), permitiendo la trazabilidad, transmisión y gestión de información asociada a operaciones como factoring, confirming, pagos B2B y negociación de instrumentos financieros, sin asumir la titularidad ni el control directo de los fondos o derechos subyacentes.",
          ],
        },
        {
          heading: "10. Cambios",
          body: [
            "Podemos actualizar esta Política. La versión vigente es siempre la publicada en esta página, con la fecha de actualización correspondiente.",
          ],
        },
      ],
    },
    cookies: {
      title: "Política de Cookies",
      updated: "24 de abril de 2026",
      intro:
        "Esta página explica qué cookies y tecnologías similares utilizamos en ibylit.com, para qué sirven y cómo puede gestionarlas, en cumplimiento de la Directiva ePrivacy de la UE, el RGPD y la Ley N.º 8968 de Costa Rica.",
      sections: [
        {
          heading: "1. Qué son las cookies",
          body: [
            "Las cookies son pequeños archivos almacenados en su navegador al visitar un sitio web. Permiten al sitio recordar información sobre su visita, como las preferencias de idioma. También utilizamos tecnologías equivalentes como localStorage.",
          ],
        },
        {
          heading: "2. Cookies y almacenamiento que usamos hoy",
          body: [
            "Esenciales — preferencia de idioma (ibylit-lang), preferencia de tema (theme), versión de defaults (ibylit-defaults-version) y el propio registro de consentimiento (ibylit-cookie-consent). Todos almacenados localmente en su navegador.",
            "Seguridad — cookies de Cloudflare Turnstile que protegen nuestros formularios frente a abuso automatizado. Son estrictamente necesarias y están exentas de consentimiento bajo la Directiva ePrivacy.",
            "Analítica — categoría preparada en nuestra arquitectura, sin scripts activos en este momento.",
            "Marketing — categoría preparada en nuestra arquitectura, sin scripts activos en este momento.",
          ],
        },
        {
          heading: "3. Arquitectura técnica de consentimiento",
          body: [
            "Hemos implementado un cargador genérico de scripts (loadWhenConsented) que actúa como única vía técnica para inicializar cualquier dependencia no esencial. Ningún recurso de analítica, marketing, píxeles publicitarios, herramientas de A/B testing o widgets de terceros puede cargarse sin pasar por este cargador.",
            "El cargador valida hasConsent(categoría) antes de inyectar el script y se suscribe al evento consent-updated para reaccionar en tiempo real cuando usted modifique sus preferencias. Esto garantiza que el rechazo de una categoría impide la ejecución del recurso, no solo su visualización.",
          ],
        },
        {
          heading: "3. Gestión de sus preferencias",
          body: [
            "Puede aceptar, rechazar o configurar las cookies no esenciales mediante el banner de consentimiento la primera vez que nos visita. También puede borrar sus preferencias en cualquier momento desde la configuración de almacenamiento de su navegador, lo que hará que el banner vuelva a aparecer en su siguiente visita.",
            "La mayoría de navegadores permiten bloquear o eliminar cookies desde su configuración. Tenga en cuenta que deshabilitar cookies esenciales puede afectar al funcionamiento del sitio.",
          ],
        },
        {
          heading: "4. Actualizaciones",
          body: [
            "Si incorporamos nuevas tecnologías o servicios de terceros, actualizaremos esta página y, cuando sea necesario, le solicitaremos nuevamente su consentimiento.",
          ],
        },
      ],
    },
    terms: {
      title: "Términos de Uso",
      updated: "24 de abril de 2026",
      intro:
        "Al acceder a ibylit.com usted acepta estos Términos de Uso. Si no está de acuerdo, le pedimos abstenerse de utilizar el sitio.",
      sections: [
        {
          heading: "1. Objeto del sitio",
          body: [
            "ibylit.com es un sitio institucional y comercial que presenta la plataforma de orquestación financiera de IBYLIT. Por sí mismo no presta servicios financieros a usuarios finales; el acceso a la plataforma se otorga bajo acuerdos contractuales separados.",
          ],
        },
        {
          heading: "2. Uso permitido",
          body: [
            "Usted se compromete a utilizar el sitio de forma lícita, sin interferir en su funcionamiento, intentar accesos no autorizados, enviar código malicioso o realizar peticiones automatizadas más allá del uso razonable.",
          ],
        },
        {
          heading: "3. Propiedad intelectual",
          body: [
            "La marca IBYLIT, los logotipos, contenidos, diseño y software del sitio están protegidos por derechos de propiedad intelectual. Queda prohibida cualquier reproducción, distribución o uso derivado sin consentimiento previo por escrito.",
          ],
        },
        {
          heading: "4. Exenciones",
          body: [
            "La información publicada en el sitio se proporciona con fines informativos generales. No constituye asesoramiento legal, fiscal, contable ni financiero. Procuramos mantenerla precisa y actualizada, pero no garantizamos la ausencia de errores ni la disponibilidad ininterrumpida del servicio.",
          ],
        },
        {
          heading: "5. Limitación de responsabilidad",
          body: [
            "En la máxima medida permitida por la ley aplicable, IBYLIT no será responsable por daños indirectos, incidentales o consecuentes derivados del uso del sitio.",
          ],
        },
        {
          heading: "6. Enlaces externos",
          body: [
            "El sitio puede contener enlaces a sitios de terceros. No nos responsabilizamos del contenido ni de las prácticas de dichos sitios.",
          ],
        },
        {
          heading: "7. Ley aplicable y jurisdicción",
          body: [
            "Estos Términos se rigen por las leyes de la República de Costa Rica. Cualquier controversia derivada del acceso o uso del sitio se someterá a los tribunales competentes de Costa Rica, sin perjuicio de los derechos imperativos que asisten a los consumidores y a los titulares de datos bajo el RGPD para usuarios ubicados en la Unión Europea.",
          ],
        },
        {
          heading: "8. Cambios",
          body: [
            "Nos reservamos el derecho de actualizar estos Términos. El uso continuado del sitio tras la publicación de cambios implica la aceptación de la nueva versión.",
          ],
        },
      ],
    },
  },
};
